// Post-test review modal for the BMD report. BmdPreviewModal runs at
// assignment time; this one renders what the student actually saw and
// answered, off `question_snapshot_json`, so edits or deletions to the source
// DQ do not break the review.
//
// When the student picked the correct answer, only their choice is marked
// ("Selected ✓"). When they picked wrong, the correct answer and their pick
// are both marked, so the tutor sees the contrast at a glance. Phases are
// grouped visually (BASE, FM_RETRY, PREREQ_L1 and so on).

import React from "react"
import { Modal, ModalBody, ModalHeader, Spinner } from "reactstrap"
import { MathJax } from "better-react-mathjax"
import { getStudentTopicAttemptDetail } from "../services/bmd-report.service"
import type {
  BmdReviewQuestion,
  BmdSnapshotAnswer,
  GetTopicAttemptDetailResponse,
} from "../types/bmd-report.types"
import { plainText } from "../utils/formatters"

const PHASE_LABELS: Record<string, string> = {
  BASE: "Base questions",
  FM_RETRY: "Fundamental Misconception retry",
}

// The student answers on a 1-5 confidence scale where 1 is the lowest
// ("I completely don't know") and 5 the highest. These mirror
// BMD_COPY.CONFIDENCE_LABELS in the student app, shortened for a table.
//
// This once rendered `answer_confident === 1 ? "Yes" : "No"`, which collapsed
// the scale to a boolean and inverted it: a student who said "I completely
// don't know" was shown to the tutor as "Confident: Yes".
const CONFIDENCE_LABELS: Record<number, string> = {
  1: "Had no idea",
  2: "Unsure it was right",
  3: "Thought it might be wrong",
  4: "Thought it was right",
  5: "Certain it was right",
}

function confidenceLabel(value: number | null | undefined): string {
  const n = Number(value)
  return CONFIDENCE_LABELS[n] || "Not recorded"
}

function phaseLabel(phase: string): string {
  if (PHASE_LABELS[phase]) return PHASE_LABELS[phase]
  if (phase.startsWith("PREREQ_L")) {
    const level = phase.slice("PREREQ_L".length)
    return `Prerequisite chain, level ${level}`
  }
  return phase
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—"
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const AnswerRow: React.FC<{
  answer: BmdSnapshotAnswer
  isChosen: boolean
  studentAnsweredCorrectly: boolean
}> = ({ answer, isChosen, studentAnsweredCorrectly }) => {
  const isCorrect = Number(answer.answer_is_correct) === 1
  // Marker policy:
  //   - answered correctly: mark only the chosen answer ("Selected ✓")
  //   - answered wrongly: mark the correct answer and the chosen one separately
  //   - not answered: mark only the correct answer, for reference
  let markerLabel: string | null = null
  let bgClass = ""
  let borderClass = "border"
  if (studentAnsweredCorrectly) {
    if (isChosen) {
      markerLabel = "Selected ✓"
      bgClass = "bg-success-subtle"
      borderClass = "border border-success"
    }
  } else {
    if (isCorrect) {
      markerLabel = "Correct answer"
      bgClass = "bg-success-subtle"
      borderClass = "border border-success"
    }
    if (isChosen) {
      markerLabel = "Student selected ✗"
      bgClass = "bg-danger-subtle"
      borderClass = "border border-danger"
    }
  }
  return (
    <div
      className={`d-flex align-items-start justify-content-between rounded p-2 mb-2 ${bgClass} ${borderClass}`}
    >
      <div className="flex-grow-1">
        <MathJax dynamic>
          <span dangerouslySetInnerHTML={{ __html: answer.answer_text }} />
        </MathJax>
      </div>
      {markerLabel ? (
        <span className="ms-3 font-size-12 fw-bold">{markerLabel}</span>
      ) : null}
    </div>
  )
}

const QuestionCard: React.FC<{ q: BmdReviewQuestion; index: number }> = ({
  q,
  index,
}) => {
  const snap = q.snapshot
  const att = q.attempt
  if (!snap) {
    return (
      <div className="rounded border p-3 mb-3 bg-light">
        <div className="text-muted font-size-13">
          Question #{index + 1}: not yet answered
        </div>
      </div>
    )
  }

  const skipped = att?.skip_reason || null
  const studentAnsweredCorrectly = att?.is_correct === true && !skipped

  return (
    <div className="rounded border p-3 mb-3 bg-white">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="text-muted text-uppercase font-size-12">
          Question #{index + 1}
        </div>
        {att?.duration_seconds != null ? (
          <div className="text-muted font-size-12">
            {att.duration_seconds}s
          </div>
        ) : null}
      </div>

      <div className="mb-3 fw-semibold">
        <MathJax dynamic>
          <span dangerouslySetInnerHTML={{ __html: snap.diagnostic_text }} />
        </MathJax>
      </div>

      <div>
        {snap.answers.map((a) => (
          <AnswerRow
            key={a.answer_id}
            answer={a}
            isChosen={att?.chosen_answer_id === a.answer_id}
            studentAnsweredCorrectly={studentAnsweredCorrectly}
          />
        ))}
      </div>

      {skipped ? (
        <div className="mt-2 font-size-13">
          <span className="badge bg-warning text-dark me-2">
            Skipped: {skipped}
          </span>
        </div>
      ) : null}

      {att && (att.answer_reason != null || att.answer_confident != null) ? (
        <div className="mt-3 p-2 rounded bg-light font-size-13">
          {att.answer_reason != null ? (
            <div>
              <span className="text-muted me-2">Reasoning:</span>
              <span>{att.answer_reason || "(no answer)"}</span>
            </div>
          ) : null}
          {att.answer_confident != null ? (
            <div>
              <span className="text-muted me-2">Confidence:</span>
              <span>{confidenceLabel(att.answer_confident)}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export type BmdAttemptReviewModalProps = {
  isOpen: boolean
  onClose: () => void
  sessionTopicKey: string | null
}

export const BmdAttemptReviewModal: React.FC<BmdAttemptReviewModalProps> = ({
  isOpen,
  onClose,
  sessionTopicKey,
}) => {
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [data, setData] = React.useState<GetTopicAttemptDetailResponse | null>(
    null,
  )

  React.useEffect(() => {
    if (!isOpen || !sessionTopicKey) return
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    setData(null)
    getStudentTopicAttemptDetail(
      { session_topic_key: sessionTopicKey },
      controller.signal,
    )
      .then((res) => {
        setData(res)
        setLoading(false)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setError(err?.message || "Failed to load attempt detail")
        setLoading(false)
      })
    return () => controller.abort()
  }, [isOpen, sessionTopicKey])

  // Group questions by phase, preserving the order the backend returned
  // (BASE first, FM_RETRY, then PREREQ_L1..N).
  const grouped: { phase: string; items: BmdReviewQuestion[] }[] = []
  if (data) {
    for (const q of data.questions) {
      const last = grouped[grouped.length - 1]
      if (last && last.phase === q.phase) {
        last.items.push(q)
      } else {
        grouped.push({ phase: q.phase, items: [q] })
      }
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" scrollable>
      <ModalHeader toggle={onClose}>
        {data ? (
          <span>
            {plainText(data.topic.topic_name)}{" "}
            <span className="text-muted font-size-13">
              {data.topic.year_level_name} · {formatDate(data.topic.attempted_on)}
            </span>
          </span>
        ) : (
          <span>Attempt Review</span>
        )}
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : error ? (
          <p className="text-danger mb-0">{error}</p>
        ) : !data ? null : (
          <>
            <div className="mb-3 d-flex flex-wrap align-items-center gap-3">
              <div>
                <div className="text-muted text-uppercase font-size-12">
                  Score
                </div>
                <div className="fw-bold font-size-16">
                  {data.totals.correct}/{data.totals.total}{" "}
                  <span className="text-muted font-size-13">
                    ({data.totals.accuracy_pct}%)
                  </span>
                </div>
              </div>
              <div>
                <div className="text-muted text-uppercase font-size-12">
                  Status
                </div>
                <div className="fw-bold font-size-16">
                  {data.topic.topic_status}
                </div>
              </div>
            </div>

            {grouped.length === 0 ? (
              <p className="text-muted mb-0">
                No questions recorded for this topic.
              </p>
            ) : (
              grouped.map((group, gi) => (
                <div key={gi} className="mb-4">
                  <div className="text-uppercase fw-bold text-muted font-size-12 mb-2">
                    {phaseLabel(group.phase)}
                  </div>
                  {group.items.map((q, idx) => (
                    <QuestionCard
                      key={q.pk_session_question_key}
                      q={q}
                      index={idx}
                    />
                  ))}
                </div>
              ))
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  )
}

export default BmdAttemptReviewModal
